import { useCallback } from "react";

import {
  citeReferences,
  type MakeCiteUrl,
} from "@tsmono/inspect-components/transcript";
import type { MarkdownReference } from "@tsmono/react/components";
import { readScannerReferences } from "@tsmono/scout-components/sentinels";

import { kSampleTranscriptTabId } from "../../../constants";
import {
  sampleEventUrl,
  sampleMessageUrl,
  useLogOrSampleRouteParams,
  useSampleUrlBuilder,
} from "../../routing/url";

import type { ScanReferencePreviews } from "./scanReferencePreviews";

type Metadata = Record<string, unknown> | null | undefined;

export function buildScoreMarkdownRefs(
  metadata: Metadata,
  makeUrl: MakeCiteUrl,
  previewTable?: ScanReferencePreviews
): MarkdownReference[] {
  return citeReferences(readScannerReferences(metadata), makeUrl).map(
    (ref) => ({ ...ref, citePreview: previewTable?.get(ref.id) })
  );
}

export type { MakeCiteUrl };

/**
 * Hook that returns a cite URL builder closing over the sample's events and
 * identifiers. Call this wherever those values are already available and pass
 * the returned function to `buildScoreMarkdownRefs` (or `SampleScansSidebar`)
 * so the score-rendering components don't have to know about navigation.
 */
export function useMakeCiteUrl(opts: {
  sampleId?: string | number;
  sampleEpoch?: number;
}): MakeCiteUrl {
  const { sampleId, sampleEpoch } = opts;
  const builder = useSampleUrlBuilder();
  const {
    logPath: urlLogPath,
    id: urlSampleId,
    epoch: urlEpoch,
  } = useLogOrSampleRouteParams();

  return useCallback(
    (id, type) => {
      if (!urlLogPath) return undefined;
      const effectiveSampleId = sampleId ?? urlSampleId;
      const effectiveEpoch = sampleEpoch ?? urlEpoch;
      // For message refs we keep the message ID in the URL — the transcript's
      // resolver runs at click time, preferring the currently selected branch
      // and falling back to other branches when needed. Event refs map
      // directly to a specific event uuid.
      const path =
        type === "event"
          ? sampleEventUrl(
              builder,
              id,
              urlLogPath,
              effectiveSampleId,
              effectiveEpoch
            )
          : sampleMessageUrl(
              builder,
              id,
              urlLogPath,
              effectiveSampleId,
              effectiveEpoch,
              kSampleTranscriptTabId
            );
      // MarkdownDivWithReferences only intercepts clicks whose href starts
      // with "#/"; plain paths would trigger a full browser navigation.
      return path ? `#${path}` : undefined;
    },
    [sampleId, sampleEpoch, builder, urlLogPath, urlSampleId, urlEpoch]
  );
}
